/**
 * Lightweight notification chime using Web Audio API.
 * No asset file required — generates a brief, pleasant two-tone ping
 * that works on iPad (Safari/Chrome) and desktop browsers.
 *
 * iOS/iPadOS requires a user gesture before the AudioContext can play.
 * We lazily create the context on first call and resume it if suspended.
 *
 * Mute preference persists in localStorage under "evo:notifSoundMuted".
 */

const MUTE_KEY = "evo:notifSoundMuted";

let ctx: AudioContext | null = null;

function getContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  const AC: typeof AudioContext | undefined =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!AC) return null;
  try {
    ctx = new AC();
  } catch {
    ctx = null;
  }
  return ctx;
}

export function isNotificationSoundMuted(): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(MUTE_KEY) === "1";
}

export function setNotificationSoundMuted(muted: boolean): void {
  if (typeof localStorage === "undefined") return;
  if (muted) localStorage.setItem(MUTE_KEY, "1");
  else localStorage.removeItem(MUTE_KEY);
}

/**
 * Play a short two-tone "ping" chime. Safe to call from any event;
 * silently no-ops if muted, audio unsupported, or context cannot resume.
 */
export async function playNotificationSound(): Promise<void> {
  if (isNotificationSoundMuted()) return;
  const audio = getContext();
  if (!audio) return;

  try {
    if (audio.state === "suspended") {
      await audio.resume();
    }
  } catch {
    return;
  }

  const now = audio.currentTime;
  // Two soft sine tones: 880 Hz then 1320 Hz, ~140 ms each, gentle attack/decay.
  const tones: Array<{ freq: number; start: number; duration: number; peak: number }> = [
    { freq: 880, start: now, duration: 0.14, peak: 0.18 },
    { freq: 1320, start: now + 0.12, duration: 0.18, peak: 0.16 },
  ];

  for (const t of tones) {
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(t.freq, t.start);
    gain.gain.setValueAtTime(0.0001, t.start);
    gain.gain.exponentialRampToValueAtTime(t.peak, t.start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t.start + t.duration);
    osc.connect(gain).connect(audio.destination);
    osc.start(t.start);
    osc.stop(t.start + t.duration + 0.02);
  }
}
